import { SingleSalesPage } from '@spike/single-sale-model';
import { Spinner } from 'components/UI/V3/Spinner/Spinner';
import { Table } from 'components/UI/V3/Table/Table';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { FunctionComponent } from 'react';
import SalesTableRow from '../TableRow/SalesTableRow';
import styles from './SalesTable.module.scss';

interface SalesTableProps {
    page?: SingleSalesPage;
    loading: boolean;
    timeZone: string;
    onSelectSale: (saleId: number) => void;
    onPageChange: (page: number) => void;
    onSizeChange: (size: number) => void;
}

const SalesTableLoading = () => (
    <TableBody>
        <tr>
            <td
                colSpan={5}
                className={styles.loadingCell}
            >
                <div className={styles.loadingContent}>
                    <Spinner size={36} />
                </div>
            </td>
        </tr>
    </TableBody>
);

export const SalesTable: FunctionComponent<SalesTableProps> = ({
    page,
    loading,
    timeZone,
    onSelectSale,
    onPageChange,
    onSizeChange
}) => {
    const hasSales = (page?.sales.length ?? 0) > 0;
    const pagination =
        !loading && page && page.pagination.totalPages > 0
            ? {
                  page: page.pagination.page,
                  totalPages: page.pagination.totalPages,
                  size: page.pagination.pageSize,
                  isLoading: false
              }
            : undefined;

    const renderBody = () => {
        if (loading) {
            return <SalesTableLoading />;
        }

        if (!hasSales) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={5}
                            className={styles.emptyCell}
                        >
                            <div className={styles.emptyState}>
                                <p className={styles.emptyTitle}>No Sales Yet</p>
                                <p className={styles.emptyDescription}>You have no registered sales</p>
                            </div>
                        </td>
                    </tr>
                </TableBody>
            );
        }

        return (
            <TableBody>
                {page!.sales.map(sale => (
                    <SalesTableRow
                        key={sale.id}
                        sale={sale}
                        timeZone={timeZone}
                        onClick={() => onSelectSale(sale.id)}
                    />
                ))}
            </TableBody>
        );
    };

    return (
        <Table
            className={styles.table}
            tableClassName={styles.tableElement}
            wrapperClassName={styles.tableWrapper}
            pagination={pagination}
            firstColumnFixed
            onPageChange={onPageChange}
            onSizeChange={onSizeChange}
        >
            <colgroup>
                <col className={styles.dateColumn} />
                <col className={styles.clientColumn} />
                <col className={styles.itemsColumn} />
                <col className={styles.totalColumn} />
                <col className={styles.statusColumn} />
            </colgroup>
            <TableHeader>
                <TableHeaderCell className={styles.dateHeader}>Date</TableHeaderCell>
                <TableHeaderCell className={styles.clientHeader}>Client</TableHeaderCell>
                <TableHeaderCell className={styles.itemsHeader}>Items</TableHeaderCell>
                <TableHeaderCell className={styles.totalHeader}>Total</TableHeaderCell>
                <TableHeaderCell className={styles.statusHeader}>Status</TableHeaderCell>
            </TableHeader>
            {renderBody()}
        </Table>
    );
};

export default SalesTable;
